import React, { useState } from "react";
import { CircleLoader } from "react-spinners";
import useRequest from "../../components/hooks/use-request";
import { formatDateLong } from "../../utilities/functions";
import { showToast } from "../../components/toast";
import ConfirmModal from "./confirm-modal";
import {
  EmailCampaign,
  currentAdminId,
  prettyStatus,
  statusClass,
  toMessage,
} from "./api";

// A datetime-local value is local wall-clock time; toISOString moves it to UTC.
const localToISO = (v: string) => (v ? new Date(v).toISOString() : undefined);
const humanUTC = (v: string) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toUTCString();
};

interface Props {
  campaign: EmailCampaign;
  onChanged: () => void | Promise<void>;
}

const CampaignActions = ({ campaign, onChanged }: Props) => {
  const id = campaign.id;
  const status = campaign.status;

  const [submitOpen, setSubmitOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");

  const { makeRequest: submitCampaign, loading: submitting } = useRequest(
    `/email-campaigns/${id}/submit`,
    "POST"
  );
  const { makeRequest: approveCampaign, loading: approving } = useRequest(
    `/email-campaigns/${id}/approve`,
    "POST"
  );
  const { makeRequest: cancelCampaign, loading: cancelling } = useRequest(
    `/email-campaigns/${id}/cancel`,
    "POST"
  );
  const run = async (
    fn: (body?: any) => Promise<any>,
    body: any,
    okMsg: string,
    failMsg: string,
    close: () => void
  ) => {
    const [res, code] = await fn(body);
    if (code >= 200 && code < 300) {
      showToast(toMessage(res, okMsg), true, { position: "top-center" });
      close();
      await onChanged();
    } else {
      showToast(toMessage(res, failMsg), false, { position: "top-center" });
    }
  };

  // The API rejects an approval by the campaign's creator with a 403
  const isOwnCampaign =
    !!campaign.createdBy && campaign.createdBy === currentAdminId();

  const canSubmit = status === "draft";
  const canApprove = status === "pending_approval";
  const canCancel = ["draft", "pending_approval", "scheduled", "sending"].includes(
    status
  );

  // Counts are meaningful once the campaign leaves draft, or whenever the
  // server has already populated them.
  const hasFigures =
    status !== "draft" ||
    campaign.recipientCount != null ||
    campaign.sentCount != null;

  const hasAudience =
    !!campaign.audienceFilter &&
    Object.keys(campaign.audienceFilter).length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[16px] font-semibold">Workflow</h3>
        <span
          className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusClass(
            status
          )}`}>
          {prettyStatus(status)}
        </span>
      </div>
      <p className="text-[13px] text-gray-500 mb-5">
        Draft → Pending approval → Scheduled → Sent. Approval must come from a
        different admin.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        {canSubmit && (
          <button
            onClick={() => setSubmitOpen(true)}
            disabled={submitting}
            className="bg-[#0979A1] text-white rounded-lg px-5 py-2.5 text-[14px] font-semibold hover:bg-[#076a8a] disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2">
            {submitting ? (
              <CircleLoader color="#ffffff" loading size={18} />
            ) : (
              "Submit for approval"
            )}
          </button>
        )}

        {canApprove && (
          <button
            onClick={() => setApproveOpen(true)}
            disabled={isOwnCampaign || approving}
            title={
              isOwnCampaign
                ? "Requires a different admin — you created this campaign"
                : "Approve and send or schedule"
            }
            className="bg-[#0979A1] text-white rounded-lg px-5 py-2.5 text-[14px] font-semibold hover:bg-[#076a8a] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {approving ? (
              <CircleLoader color="#ffffff" loading size={18} />
            ) : (
              "Approve"
            )}
          </button>
        )}

        {canCancel && (
          <button
            onClick={() => setCancelOpen(true)}
            disabled={cancelling}
            className="px-5 py-2.5 border border-[#A10909] text-[#A10909] rounded-lg text-[14px] font-semibold hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed">
            Cancel campaign
          </button>
        )}

        {status === "sent" && (
          <p className="text-[13px] text-gray-500">
            This campaign has been sent.
          </p>
        )}
      </div>

      {canApprove && isOwnCampaign && (
        <p className="text-[12px] text-amber-700 mt-3">
          You created this campaign, so another admin has to approve it.
        </p>
      )}
      {canApprove && !hasAudience && (
        <p className="text-[12px] text-amber-700 mt-2">
          No audience filter is set — approval will fail until the audience
          matches at least one recipient.
        </p>
      )}

      {/* Headline figures — these ride on the campaign record itself, so they
          are available as soon as it is created, with no extra call. */}
      {hasFigures && (
        <div className="mt-6 border-t border-gray-100 pt-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-gray-100 rounded-lg px-3 py-2">
              <p className="text-[18px] font-bold leading-tight">
                {(campaign.recipientCount ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-500">Recipients</p>
            </div>
            <div className="border border-gray-100 rounded-lg px-3 py-2">
              <p className="text-[18px] font-bold leading-tight text-green-700">
                {(campaign.sentCount ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-500">Sent</p>
            </div>
            <div className="border border-gray-100 rounded-lg px-3 py-2">
              <p className="text-[18px] font-bold leading-tight text-red-600">
                {(campaign.failedCount ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-500">Failed</p>
            </div>
          </div>

          {(campaign.approvedAt || campaign.cancelledAt || campaign.scheduledAt) && (
            <div className="mt-3 space-y-0.5">
              {campaign.scheduledAt && (
                <p className="text-[11px] text-gray-500">
                  Scheduled for {formatDateLong(campaign.scheduledAt)}
                </p>
              )}
              {campaign.approvedAt && (
                <p className="text-[11px] text-gray-500">
                  Approved {formatDateLong(campaign.approvedAt)}
                </p>
              )}
              {campaign.cancelledAt && (
                <p className="text-[11px] text-gray-500">
                  Cancelled {formatDateLong(campaign.cancelledAt)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Confirmations ─────────────────────────────────────────────── */}
      <ConfirmModal
        visible={submitOpen}
        title="Submit for approval"
        message={
          <>
            Submit <strong>{campaign.name}</strong> for approval? You will not
            be able to edit it until it is approved or cancelled.
          </>
        }
        confirmLabel="Submit"
        loading={submitting}
        onConfirm={() =>
          run(
            submitCampaign,
            undefined,
            "Campaign submitted for approval",
            "Failed to submit campaign",
            () => setSubmitOpen(false)
          )
        }
        onClose={() => setSubmitOpen(false)}
      />

      <ConfirmModal
        visible={approveOpen}
        title="Approve campaign"
        message={
          <div className="text-left">
            <p className="text-center mb-4">
              Approving <strong>{campaign.name}</strong> sends real email to
              every matching recipient.
            </p>
            <label className="text-[13px] font-medium text-[#040821] mb-1.5 block">
              Schedule for (optional)
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full h-[40px] px-3 border border-[#0979A1] rounded-lg text-[13px] outline-none focus:border-[#0979A1]"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              {scheduledAt
                ? `Sends at ${humanUTC(scheduledAt)}`
                : "Leave empty to send as soon as it is approved."}
            </p>
          </div>
        }
        confirmLabel={scheduledAt ? "Approve & schedule" : "Approve & send"}
        loading={approving}
        onConfirm={() =>
          run(
            approveCampaign,
            scheduledAt ? { scheduledAt: localToISO(scheduledAt) } : {},
            scheduledAt ? "Campaign scheduled" : "Campaign approved",
            "Failed to approve campaign",
            () => setApproveOpen(false)
          )
        }
        onClose={() => setApproveOpen(false)}
      />

      <ConfirmModal
        visible={cancelOpen}
        title="Cancel campaign"
        message={
          <>
            Cancel <strong>{campaign.name}</strong>? This stops it from being
            sent and cannot be undone.
          </>
        }
        confirmLabel="Cancel campaign"
        danger
        loading={cancelling}
        onConfirm={() =>
          run(
            cancelCampaign,
            undefined,
            "Campaign cancelled",
            "Failed to cancel campaign",
            () => setCancelOpen(false)
          )
        }
        onClose={() => setCancelOpen(false)}
      />
    </div>
  );
};

export default CampaignActions;
